/**
 * Geometry utility functions for visualization domain objects.
 * Uses vtkMath from @kitware/vtk.js for vector operations.
 */

import vtkMath from '@kitware/vtk.js/Common/Core/Math';

import type { Vec3, MutableVec3 } from './types';

/** Convert readonly Vec3 to mutable array for vtkMath */
function toMutable(v: Vec3): MutableVec3 {
    return [v[0], v[1], v[2]];
}

/**
 * Calculate the cross product of two vectors.
 */
export function cross(a: Vec3, b: Vec3): Vec3 {
    const out: MutableVec3 = [0, 0, 0];
    vtkMath.cross(toMutable(a), toMutable(b), out);
    return out;
}

/**
 * Normalize a vector to unit length.
 * Returns a new normalized vector.
 */
export function normalize(v: Vec3): Vec3 {
    const out: MutableVec3 = toMutable(v);
    vtkMath.normalize(out);
    return out;
}

/**
 * Subtract vector b from vector a.
 */
export function subtract(a: Vec3, b: Vec3): Vec3 {
    const out: MutableVec3 = [0, 0, 0];
    vtkMath.subtract(toMutable(a), toMutable(b), out);
    return out;
}

/**
 * Add two vectors.
 */
export function add(a: Vec3, b: Vec3): Vec3 {
    const out: MutableVec3 = [0, 0, 0];
    vtkMath.add(toMutable(a), toMutable(b), out);
    return out;
}

/**
 * Scale a vector by a scalar.
 */
export function scale(v: Vec3, s: number): Vec3 {
    const out: MutableVec3 = toMutable(v);
    vtkMath.multiplyScalar(out, s);
    return out;
}

/**
 * Calculate the dot product of two vectors.
 */
export function dot(a: Vec3, b: Vec3): number {
    return vtkMath.dot(toMutable(a), toMutable(b));
}

/**
 * Calculate the distance between two points.
 */
export function distance(a: Vec3, b: Vec3): number {
    return Math.sqrt(vtkMath.distance2BetweenPoints(toMutable(a), toMutable(b)));
}

/**
 * Calculate the squared distance between two points.
 */
export function squaredDistance(a: Vec3, b: Vec3): number {
    return vtkMath.distance2BetweenPoints(toMutable(a), toMutable(b));
}

/**
 * Linear interpolation between two vectors.
 */
export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Calculate the length (norm) of a vector.
 */
export function length(v: Vec3): number {
    return vtkMath.norm(toMutable(v), 3);
}

/**
 * Calculate area of a 3D polygon using the shoelace formula.
 * The polygon is assumed to be planar.
 */
export function calculatePolygonArea(vertices: readonly Vec3[]): number {
    if (vertices.length < 3) return 0;

    // Calculate normal from first 3 vertices
    const v0 = vertices[0];
    const v1 = vertices[1];
    const v2 = vertices[2];

    const edge1 = subtract(v1, v0);
    const edge2 = subtract(v2, v0);
    const normal = normalize(cross(edge1, edge2));

    // Sum cross products
    let total: MutableVec3 = [0, 0, 0];
    for (let i = 0; i < vertices.length; i++) {
        const current = vertices[i];
        const next = vertices[(i + 1) % vertices.length];
        const crossProduct = cross(current, next);
        total = add(total, crossProduct) as MutableVec3;
    }

    return Math.abs(dot(total, normal)) / 2;
}

/**
 * Project a point onto a plane defined by an origin and normal.
 */
export function projectPointOntoPlane(point: Vec3, planeOrigin: Vec3, planeNormal: Vec3): Vec3 {
    const toPoint = subtract(point, planeOrigin);
    const dist = dot(toPoint, planeNormal);
    const projection = scale(planeNormal, dist);
    return subtract(point, projection);
}

/**
 * Binary search to find the index of the closest value in a sorted array.
 */
export function binarySearchClosest(arr: readonly number[], target: number): number {
    if (arr.length === 0) return -1;
    if (arr.length === 1) return 0;

    let low = 0;
    let high = arr.length - 1;

    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (arr[mid] < target) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    // Check if previous index is closer
    if (low > 0 && Math.abs(arr[low - 1] - target) < Math.abs(arr[low] - target)) {
        return low - 1;
    }

    return low;
}

/**
 * Check if a 2D point lies inside a polygon (2D version for projected points).
 * Uses ray casting algorithm.
 */
export function isPointInPolygon2D(
    point: readonly [number, number],
    polygon: readonly (readonly [number, number])[]
): boolean {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];

        if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }

    return inside;
}
